import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { Col, Row } from "~/common/components/Layout";
import Modal from "~/common/components/Modal";
import { ParamComponent } from "~/common/components/ParamsGrid";
import classes from "./AddCommentModal.module.less";
import { type CaseDataObject, CaseStatus } from "./types";
import { format } from "~/api/ApiHelper";
import { TextField } from "@mui/material";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface AddCommentModalProps {
  onClose: () => void;
  onSend: (code: string, message: string) => void;
  caseData?: CaseDataObject;
  className?: string;
}

interface FormData {
  feedbackMessage: string;
}

const AddCommentModal = ({ caseData, onSend, onClose, className }: AddCommentModalProps) => {
  const { t } = useTranslation();
  const { control, handleSubmit, formState } = useForm<FormData>({ defaultValues: { feedbackMessage: "" } });

  const onSubmit = async (data: FormData) => {
    if (caseData) {
      onSend(caseData.code, data.feedbackMessage);
    }
    onClose();
  };

  const FooterButton = (
    <div className="d-flex flex-justify-between flex-align-center w-100">
      <p>{t("checkoutPage.mandatory")}</p>
      <Stacker direction={"row"} gap={8}>
        <Button onClick={onClose} variant="text">
          {t("page.orderQuoteSettings.modal.button.cancel")}
        </Button>
        <Button disabled={!formState.isValid} onClick={() => handleSubmit(onSubmit)()} fullWidth>
          {t("request.information.send")}
        </Button>
      </Stacker>
    </div>
  );

  const formatTime = (timeStamp: string) => {
    return format(new Date(timeStamp), "DD.MM.YYYY");
  };

  if (!caseData) {
    return null;
  }

  return (
    <Modal
      modalClassName={className}
      onClose={onClose}
      footerChildren={FooterButton}
      title={t("addComment.header")}
    >
      <form className="mb-3">
        <Row>
          <Col size={4}>
            <ParamComponent label={t("addComment.caseNumber")} value={caseData?.sfCaseNumber} />
          </Col>
          <Col size={6}>
            <ParamComponent label={t("addComment.subject")} value={caseData?.reasonOfRequest} />
          </Col>
        </Row>
        <Row className={"mb-4"}>
          <Col size={4}>
            <ParamComponent label={t("addComment.caseStatus")} value={t(CaseStatus[caseData.status])} />
          </Col>
          <Col size={4}>
            <ParamComponent label={t("addComment.createdAt")} value={formatTime(caseData.createdAt)} />
          </Col>
          <Col size={4}>
            <ParamComponent label={t("addComment.updatedAt")} value={formatTime(caseData.updatedAt)} />
          </Col>
        </Row>
        <Controller
          render={({ field: { ref, ...props }, fieldState: { error } }) => (
            <TextField
              variant={"outlined"}
              inputRef={ref}
              className={classes.inputStyle}
              placeholder={t("addComment.placeholder") + "*"}
              helperText={t("order.information.header.helper")}
              inputProps={{ sx: { height: "96px !important" }, maxLength: 512 }}
              multiline
              fullWidth
              error={!!error}
              {...props}
            />
          )}
          name={"feedbackMessage"}
          control={control}
          rules={{ required: true, maxLength: 512 }}
          defaultValue={""}
        />
      </form>
    </Modal>
  );
};

export default AddCommentModal;
